<script lang="ts">
    import { onDestroy, onMount } from 'svelte/internal'
    import { Profile } from '../../../modules/toolsApi/sites/profiles/profile'
    import Zframe from '../../_common/z/zframe.svelte'
    import Zinput from '../../_common/z/zinput.svelte'
    import ZContainer from '../../_common/z/z-container.svelte'
    import ZinputTitle from '../../_common/z/zinput-title.svelte'

    let siteUrl = ''
    if (location.href.includes('127.0.0') || location.href.includes('localhost')) {
        siteUrl = 'http://localhost:5173/'
    } else {
        siteUrl = 'https://agenda.zandu.com.br/'
    }

    let show = true
    let entity: Profile = null
    let saving = false
    let savedOnce = false
    // o iframe é de outra origem, então não dá pra chamar reload() nele: a prévia é recriada
    // trocando esta chave, que remonta o elemento e refaz a requisição
    let previewVersion = 0

    /**
     * Espelho de THEME_UI em zandu-app-customer (src/lib/theme/presets.ts).
     *
     * Cada tema usa um subconjunto dos campos de aparência: o Sereno, por
     * exemplo, funde o cabeçalho ao fundo do conteúdo, então perguntar as cores
     * do cabeçalho seria pedir um dado que o app vai ignorar. A tabela mora lá
     * junto dos presets, que são a fonte da verdade -- ao adicionar um tema,
     * copie a entrada para cá.
     *
     * Tema desconhecido cai no comportamento completo: mostrar tudo é o padrão
     * seguro, porque esconder um campo que o tema usa é pior do que exibir um a
     * mais.
     */
    const themes = [
        {
            value: 'curvas',
            label: 'Curvas',
            headerColors: true,
            usesPictureAsCover: false,
        },
        {
            value: 'linha',
            label: 'Linha',
            headerColors: true,
            usesPictureAsCover: false,
        },
        {
            value: 'sereno',
            label: 'Sereno',
            headerColors: false,
            usesPictureAsCover: false,
        },
        {
            value: 'bloco',
            label: 'Bloco',
            headerColors: true,
            usesPictureAsCover: true,
        },
        {
            value: 'modern',
            label: 'Moderno',
            headerColors: false,
            usesPictureAsCover: true,
        },
        {
            value: 'glass',
            label: 'Vidro',
            headerColors: false,
            usesPictureAsCover: false,
        },
    ]

    const headerFormatOptions = themes.map((t) => ({ value: t.value, label: t.label }))

    // no type="file" o Zinput usa "options" como lista de padrões do accept nativo, não como escolhas
    const imageAccept = [{ value: 'image/*', label: 'Imagens' }]

    const fallbackTheme = { headerColors: true, usesPictureAsCover: false }

    $: theme = themes.find((t) => t.value === entity?.themeHeaderFormat) || fallbackTheme

    onMount(async () => {
        await loadEntity()
    })

    onDestroy(() => {
        clearTimeout(saveTimer)
    })

    async function loadEntity() {
        try {
            show = true
            entity = await window.api.tools.sites.profiles.getOne(null)
        } catch (error) {
            window.error(error)
        } finally {
            show = false
        }
    }

    // nesta aba não existe botão salvar: cada alteração grava sozinha para a prévia refletir na hora.
    // o debounce agrupa mudanças em sequência (trocar tema e cor logo em seguida) numa gravação só
    let saveTimer = null
    function onChange() {
        clearTimeout(saveTimer)
        saveTimer = setTimeout(saveTheme, 500)
    }

    async function saveTheme() {
        if (!entity) return
        try {
            saving = true
            // só os campos de aparência: o PATCH é parcial, então o resto do perfil não é tocado
            const dto = {
                profilePicture: entity.profilePicture,
                themeHeaderFormat: entity.themeHeaderFormat,
                themeHeaderTextColor: entity.themeHeaderTextColor,
                themeHeaderBackgroundColor: entity.themeHeaderBackgroundColor,
                themeTextColor: entity.themeTextColor,
                themeBackgroundColor: entity.themeBackgroundColor,
                themePrimaryColor: entity.themePrimaryColor,
            }
            await window.api.tools.sites.profiles.update(entity.companyId, dto)
            savedOnce = true
            previewVersion++
        } catch (error) {
            window.error(error)
        } finally {
            saving = false
        }
    }
</script>

{#if !show}
    {#if !entity}
        <Zframe class="h-space" title="Aparência">
            <div class="text-muted">Seu aplicativo ainda não foi criado. Preencha os dados em "Meu aplicativo" e salve para poder configurar a aparência.</div>
        </Zframe>
    {:else}
        <div class="appearance">
            <div class="appearance_form">
                <Zframe class="h-space" title="Aparência" headline="Configure a aparência de seu app">
                    <ZContainer gapRow="8px" gapCol="8px">
                        <Zinput
                            size={4}
                            sizeM={12}
                            label="Imagem de perfil"
                            type="file"
                            presign={false}
                            options={imageAccept}
                            bind:value={entity.profilePicture}
                            on:change={onChange}
                        />
                        <!-- o tema vem logo depois da imagem porque define a aparência do app inteiro, e não só a do cabeçalho -->
                        <Zinput
                            size={4}
                            sizeM={12}
                            label="Tema"
                            type="select"
                            options={headerFormatOptions}
                            bind:value={entity.themeHeaderFormat}
                            on:change={onChange}
                        />

                        {#if theme.headerColors}
                            <ZinputTitle
                                title="Cabeçalho"
                                label={theme.usesPictureAsCover ? 'Neste tema a foto do seu perfil é usada como capa do topo.' : ''}
                            />
                            <Zinput size={4} sizeM={12} label="Cor do texto" type="color" bind:value={entity.themeHeaderTextColor} on:change={onChange} />
                            <Zinput size={4} sizeM={12} label="Cor de fundo" type="color" bind:value={entity.themeHeaderBackgroundColor} on:change={onChange} />
                        {:else}
                            <!-- a seção continua visível, sem campos: some a dúvida de "onde foi parar a cor do cabeçalho" -->
                            <ZinputTitle title="Cabeçalho" label="Neste tema o cabeçalho usa as mesmas cores do conteúdo, sem cores próprias." />
                        {/if}

                        <ZinputTitle title="Conteúdo" />
                        <Zinput size={4} sizeM={12} label="Cor do texto" type="color" bind:value={entity.themeTextColor} on:change={onChange} />
                        <Zinput size={4} sizeM={12} label="Cor de fundo" type="color" bind:value={entity.themeBackgroundColor} on:change={onChange} />
                        <Zinput size={4} sizeM={12} label="Cor dos elementos" type="color" bind:value={entity.themePrimaryColor} on:change={onChange} />
                    </ZContainer>
                </Zframe>

                <div class="appearance_status">
                    {#if saving}
                        Salvando…
                    {:else if savedOnce}
                        Alterações salvas
                    {:else}
                        As alterações são salvas automaticamente
                    {/if}
                </div>
            </div>

            <div class="appearance_preview">
                {#key previewVersion}
                    <iframe src="{siteUrl}{entity.slug}?preview=1" frameborder="0" title="Prévia do seu aplicativo" />
                {/key}
            </div>
        </div>
    {/if}
{/if}

<style>
    .appearance {
        display: flex;
        align-items: flex-start;
    }

    .appearance_form {
        flex: 1 1 auto;
        min-width: 0px;
    }

    .appearance_status {
        margin-top: 8px;
        font-size: 13px;
        opacity: 0.7;
    }

    /* a prévia acompanha a rolagem para continuar visível enquanto as cores são ajustadas */
    .appearance_preview {
        flex: 0 0 390px;
        position: sticky;
        top: 16px;
        margin-top: 12px;
        margin-left: 16px;
    }

    .appearance_preview iframe {
        width: 100%;
        height: calc(100vh - 230px);
        min-height: 520px;
        border-radius: 8px;
        border: 2px solid #666666;
    }

    @media (max-width: 900px) {
        .appearance {
            flex-direction: column;
        }
        .appearance_preview {
            flex: 1 1 auto;
            width: 100%;
            position: static;
            margin-left: 0px;
        }
    }
</style>
